/* Humbler skeletons + image loading state. Loaded with `defer` on every page (layout/theme.liquid).
   Styles: the "Skeletons + image loading state" block at the end of assets/humbler-base.css.
   1. Images: every <img> gets .is-loaded once it has loaded or failed — capture-phase load/error
      listeners, a sweep of images that were already done (or already painting) before this ran, and a
      MutationObserver for images added later (bag, set builder, infinite scroll). Only then is
      html.hb-img-ready set, which lets the CSS fade lazy images in and shimmer their .hb-stage, so
      cached images never flash.
   2. window.HumblerSkeleton:
      cards(count, { tag }) -> HTML of `count` skeleton product cards (<li data-hb-skeleton aria-hidden>
                               holding .hb-card.hb-card--skeleton; tag 'div' for non-list parents)
      clear(scope)          -> removes every [data-hb-skeleton] node inside scope
      markLoaded(scope)     -> marks finished images inside scope (after moving nodes around)
      swapSrc(img, src)     -> changes an image's source, showing the stage shimmer until the new file is in
      warm(images, limit)   -> starts loading lazy images early (hover/focus intent), first `limit` only
   3. App widgets (Judge.me): a [data-hb-app-wait="<selector>"] container keeps its [data-hb-app-skeleton]
      until an element matching the selector inside it (or any other content of it) has a size. With
      [data-hb-app-strict] only the selector counts (the app draws in stages and the first ones are not the
      finished size); [data-hb-app-wait-basic] is the selector used where the main one is not supported
      (no :has()). If the app never renders (blocked script, no consent), the skeleton is removed after the
      window load event + 6s (15s at most), keeping whatever the app did draw. Only when nothing was drawn by
      then and the [data-hb-app-scope] has [data-hb-app-fallback] content (hidden in the markup until this point)
      is that content shown, while the app container is set aside with [data-hb-app-failed]: still laid out but
      out of sight and taking no space (assets/humbler-reviews.css), so an app that draws late is still seen and
      swapped back in. Each swap is one step, and a swap above the viewport keeps the page where it was.
   4. Read ahead: lazy images in <main> start loading 1.5 screens before they scroll into view. */
(() => {
  if (window.HumblerSkeleton) return;
  const html = document.documentElement;

  /* ---------- 1. images ---------- */
  const mark = img => {
    img.classList.add('is-loaded');
    img.classList.remove('is-swapping');
  };
  const onDone = event => { if (event.target instanceof HTMLImageElement) mark(event.target); };
  document.addEventListener('load', onDone, true);
  document.addEventListener('error', onDone, true);

  const markLoaded = (scope = document) => {
    const images = scope instanceof HTMLImageElement ? [scope] : scope.querySelectorAll?.('img') || [];
    for (const img of images) if (img.complete && !img.classList.contains('is-swapping')) mark(img);
  };

  // Images that finished (or started painting) before this script ran are never hidden again.
  for (const img of document.images) if (img.complete || img.naturalWidth > 0) mark(img);
  html.classList.add('hb-img-ready');

  // Images added later: a cached file is complete straight away; mark it before the first paint.
  if ('MutationObserver' in window) {
    new MutationObserver(records => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'IMG') markLoaded(node);
          else if (node.firstElementChild) markLoaded(node);
        }
      }
    }).observe(html, { childList: true, subtree: true });
  }

  function swapSrc(img, src) {
    if (!img || !src || img.getAttribute('src') === src) return;
    img.classList.remove('is-loaded');
    img.classList.add('is-swapping');
    img.removeAttribute('srcset');
    img.src = src;
    if (img.complete) mark(img);
  }

  // Warmed images keep the lazy fade (data-hb-warm stands in for loading=lazy in the CSS).
  function warm(images, limit = 4) {
    let count = 0;
    for (const img of images) {
      if (count >= limit) break;
      if (img.classList.contains('hb-card__image--alt')) continue;
      count += 1;
      if (img.loading !== 'lazy') continue;
      if (!img.classList.contains('is-loaded')) img.setAttribute('data-hb-warm', '');
      img.loading = 'eager';
    }
  }

  /* ---------- 2. skeleton cards ---------- */
  const TITLE = [72, 58, 66, 50];
  const META = [44, 36, 48, 40];
  const PRICE = [30, 26, 34, 28];
  function cards(count, { tag = 'li' } = {}) {
    const total = Math.max(0, Math.floor(Number(count) || 0));
    let out = '';
    for (let index = 0; index < total; index += 1) {
      const n = index % 4;
      out += `<${tag} class="hb-grid__skeleton" data-hb-skeleton aria-hidden="true"><div class="hb-card hb-card--skeleton">`
        + '<div class="hb-card__visual"><span class="hb-card__media hb-skeleton"></span></div>'
        + '<div class="hb-card__info">'
        + `<span class="hb-card__title"><span class="hb-skeleton hb-skeleton--text" style="--w: ${TITLE[n]}%"></span></span>`
        + `<span class="hb-card__meta"><span class="hb-skeleton hb-skeleton--text" style="--w: ${META[n]}%; --h: 10px"></span></span>`
        + `<span class="hb-card__price"><span class="hb-skeleton hb-skeleton--text" style="--w: ${PRICE[n]}%"></span></span>`
        + `</div></div></${tag}>`;
    }
    return out;
  }
  const clear = (scope = document) => scope.querySelectorAll('[data-hb-skeleton]').forEach(node => node.remove());

  /* ---------- 3. app widgets (Judge.me) ---------- */
  let loaded = document.readyState === 'complete';
  const whenLoaded = [];
  if (!loaded) window.addEventListener('load', () => { loaded = true; whenLoaded.splice(0).forEach(run => run()); }, { once: true });

  function watchApp(box) {
    if (box.hbAppWatched) return;
    box.hbAppWatched = true;
    const skeleton = box.querySelector(':scope > [data-hb-app-skeleton]');
    if (!skeleton) return;
    // Busy only while this script is waiting on the app (set here, not in Liquid: without JS it never clears).
    box.setAttribute('aria-busy', 'true');
    let selector = box.dataset.hbAppWait || '.jdgm-carousel, .jdgm-rev-widg';
    try { box.querySelector(selector); } catch { selector = box.dataset.hbAppWaitBasic || '.jdgm-carousel, .jdgm-rev-widg'; }
    const strict = box.hasAttribute('data-hb-app-strict');
    const targets = () => [...box.querySelectorAll(selector)].filter(node => !skeleton.contains(node));
    const scope = box.closest('[data-hb-app-scope]');
    let done = false;
    let failed = null; // the fallback on show while the app is set aside
    let resize = null;
    let mutations = null;
    const timers = [];
    const stopWatching = () => {
      resize?.disconnect();
      mutations?.disconnect();
    };
    // One-step swap. When the scope starts above the viewport (scrolled past), its bottom edge is held in place so
    // the content being read does not jump; where the browser's own scroll anchoring already did that, this is 0.
    const steady = change => {
      const edge = scope || box;
      const before = edge.getBoundingClientRect();
      change();
      if (before.top >= 0) return;
      const shift = edge.getBoundingClientRect().bottom - before.bottom;
      if (Math.abs(shift) >= 1) window.scrollBy({ top: shift, behavior: 'instant' });
    };
    const finish = rendered => {
      if (done) return;
      done = true;
      timers.forEach(clearTimeout);
      const fallback = rendered ? null : scope?.querySelector('[data-hb-app-fallback]');
      if (!fallback) stopWatching(); // otherwise keep watching: a late app replaces the fallback (recover)
      steady(() => {
        skeleton.remove();
        box.removeAttribute('aria-busy');
        if (!fallback) return;
        box.setAttribute('data-hb-app-failed', '');
        fallback.hidden = false;
      });
      if (!fallback) return;
      failed = fallback;
      window.HumblerHomeScrollers?.scan?.(fallback.parentElement || document);
    };
    // The app drew after all: back to the app, fallback hidden again (after the shopper's focus has left it).
    let held = false;
    const recover = () => {
      if (failed.contains(document.activeElement)) {
        if (!held) failed.addEventListener('focusout', () => { held = false; setTimeout(check); }, { once: true });
        held = true;
        return;
      }
      stopWatching();
      const fallback = failed;
      failed = null;
      steady(() => {
        box.removeAttribute('data-hb-app-failed');
        fallback.hidden = true;
      });
    };
    // Rendered = a known widget node has a size, or any other content of the slot does (Judge.me's newer
    // widgets draw their own markup next to the hidden legacy one; before its script runs, all of it is 0px).
    const others = () => [...box.children].filter(node => node !== skeleton);
    const drawn = () => others().some(node => node.getBoundingClientRect().height > 24);
    const rendered = () => targets().some(node => node.getBoundingClientRect().height > 0) || (!strict && drawn());
    function check() {
      if (!rendered()) return;
      if (failed) recover();
      else if (!done) finish(true);
    }
    if ('ResizeObserver' in window) resize = new ResizeObserver(check);
    const observe = () => [...targets(), ...others()].forEach(node => resize?.observe(node));
    if ('MutationObserver' in window) {
      mutations = new MutationObserver(() => { observe(); check(); });
      mutations.observe(box, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });
    }
    observe();
    check();
    if (done) return;
    const stop = () => finish(rendered() || drawn());
    const giveUp = () => timers.push(setTimeout(stop, Number(box.dataset.hbAppTimeout) || 6000));
    if (loaded) giveUp(); else whenLoaded.push(giveUp);
    timers.push(setTimeout(stop, 15000));
  }
  const scanApps = (scope = document) => scope.querySelectorAll('[data-hb-app-wait]').forEach(watchApp);
  scanApps();
  document.addEventListener('shopify:section:load', event => scanApps(event.target));

  /* ---------- 4. read ahead: lazy images in the page start loading 1.5 screens before they scroll in ----------
     Browsers start lazy images at different distances (Safari's is short), so photos further down could still be
     shimmering when they arrived and then fade in one by one. Card hover photos stay lazy (they load on intent). */
  if ('IntersectionObserver' in window) {
    const ahead = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        ahead.unobserve(entry.target);
        warm([entry.target], 1);
      }
    }, { rootMargin: '50% 0px 150% 0px', scrollMargin: '0px 100%' });
    const readAhead = (scope = document) => scope.querySelectorAll('main img[loading="lazy"]:not(.hb-card__image--alt)').forEach(img => ahead.observe(img));
    readAhead();
    document.addEventListener('shopify:section:load', event => readAhead(event.target));
  }

  window.HumblerSkeleton = Object.freeze({ cards, clear, markLoaded, swapSrc, warm, scanApps });
})();
